import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Card, Button, Autocomplete, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ContentCopy, Email, FilterList, Visibility, Download } from '@mui/icons-material';
import { getInfluencers } from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

function IYData({ user }) {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ state: null, city: null, category: null });

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const response = await getInfluencers();
      setInfluencers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = (rowId) => {
    const formUrl = `${process.env.REACT_APP_BASE_URL}/#/edit_form/${rowId}`;
    navigator.clipboard.writeText(formUrl);
    toast.success('Form URL copied to clipboard!');
  };

  const handleSendEmail = async (rowId) => {
    try {
      const inf = influencers.find(i => i._id === rowId);
      if (!inf?.email) {
        toast.error('No email found for this influencer');
        return;
      }
      const { default: api } = await import('../services/api');
      const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/0/${rowId}`;
      await api.post('/send-form-email', { email: inf.email, formUrl });
      toast.success('Email sent successfully!');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handleExport = () => {
    if (activeTab === 'completed') {
      const data = rows.map((row, index) => {
        const influencer = row.allData;
        const ocr = influencer?.instagramOcrSchema;
        const ytOcr = influencer?.youtubeOcrSchema;
        const topLoc = ocr?.topLocations?.length
          ? ocr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join(', ')
          : '-';
        const topLocYoutube = ytOcr?.topLocations?.length
          ? ytOcr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join(', ')
          : '-';

        const igEngagement = (() => {
          const likes = Number(influencer?.instagramLikes) || 0;
          const comments = Number(influencer?.instagramComments) || 0;
          const saves = Number(influencer?.instagramSaves) || 0;
          const shares = Number(influencer?.instagramShares) || 0;
          const reposts = Number(influencer?.instagramReposts) || 0;
          const totalReach = Number(influencer?.last10ReelsReach);
          if (!totalReach || isNaN(totalReach)) return '-';
          const rate = ((likes + comments + saves + shares + reposts) / totalReach) * 100;
          return Number(rate.toFixed(2));
        })();

        const ytEngagement = (() => {
          const likes = Number(influencer?.youtubeLikes) || 0;
          const comments = Number(influencer?.youtubeComments) || 0;
          const shares = Number(influencer?.youtubeShares) || 0;
          const totalReach = Number(influencer?.last10ShortsReach);
          if (!totalReach || isNaN(totalReach)) return '-';
          const rate = ((likes + comments + shares) / totalReach) * 100;
          return Number(rate.toFixed(2));
        })();

        const igAvgView = (() => {
          const v = Number(influencer?.last10ReelsViews);
          if (!v || isNaN(v)) return '-';
          const count = Number(influencer?.instagramNoOfPost) || 10;
          return Math.floor(v / count);
        })();

        const ytAvgView = (() => {
          const v = Number(influencer?.last10ShortsViews);
          if (!v || isNaN(v)) return '-';
          const count = Number(influencer?.youtubeNoOfPost) || 10;
          return Math.floor(v / count);
        })();

        return {
          'SNO': index + 1,
          'Channel (IG/YT)': influencer.socialMediaPlatforms || '-',
          'Influencer ID': influencer.influencerCode || '-',
          'Onboard Name': influencer.onboarderId?.name || '-',
          'Name': influencer.name || '-',
          'Email': influencer.email || '-',
          'Profile Link (IN)': influencer.instagramLink || '-',
          'Profile Link (YT)': influencer.youtubeLink || '-',
          'Location': influencer.location || '-',
          'State': influencer.state || '-',
          'City': influencer.city || '-',
          'Age': influencer.age || '-',
          'Gender': influencer.gender || '-',
          'Category': influencer.category || '-',
          'Top Locations (IN)': topLoc,
          'Female Gender Ratio (IN)': ocr?.genderDistribution?.female != null ? ocr.genderDistribution.female + '%' : '-',
          'Male Gender Ratio (IN)': ocr?.genderDistribution?.male != null ? ocr.genderDistribution.male + '%' : '-',
          '18-24 Age Range (IN)': ocr?.ageDistribution?.[1]?.percentage != null ? ocr.ageDistribution[1].percentage + '%' : '-',
          '25-34 Age Range (IN)': ocr?.ageDistribution?.[2]?.percentage != null ? ocr.ageDistribution[2].percentage + '%' : '-',
          '35-44 Age Range (IN)': ocr?.ageDistribution?.[3]?.percentage != null ? ocr.ageDistribution[3].percentage + '%' : '-',
          '45-54 Age Range (IN)': ocr?.ageDistribution?.[4]?.percentage != null ? ocr.ageDistribution[4].percentage + '%' : '-',
          '55+ Age Range (IN)': ocr?.ageDistribution?.[5]?.percentage != null ? ocr.ageDistribution[5].percentage + '%' : '-',
          'Engagement Rate per Reach (IN)': igEngagement,
          'Instagram Followers': influencer.instagramFollowers || '-',
          'Avg Reels Views': igAvgView,
          '30 days Reels Views': influencer.last10ReelsViews || '-',
          'Instagram Reach': influencer.last10ReelsReach || '-',
          'Instagram Likes': influencer.instagramLikes || '-',
          'Instagram Comments': influencer.instagramComments || '-',
          'Instagram Saves': influencer.instagramSaves || '-',
          'Instagram Shares': influencer.instagramShares || '-',
          'Instagram Reposts': influencer.instagramReposts || '-',
          'Total Cost Instagram': influencer.reelCost ? `₹${Number(influencer.reelCost).toLocaleString()}` : '-',
          'Top Locations (YT)': topLocYoutube,
          'Female Gender Ratio (YT)': ytOcr?.genderDistribution?.female != null ? ytOcr.genderDistribution.female + '%' : '-',
          'Male Gender Ratio (YT)': ytOcr?.genderDistribution?.male != null ? ytOcr.genderDistribution.male + '%' : '-',
          '18-24 Age Range (YT)': ytOcr?.ageDistribution?.[1]?.percentage != null ? ytOcr.ageDistribution[1].percentage + '%' : '-',
          '25-34 Age Range (YT)': ytOcr?.ageDistribution?.[2]?.percentage != null ? ytOcr.ageDistribution[2].percentage + '%' : '-',
          '35-44 Age Range (YT)': ytOcr?.ageDistribution?.[3]?.percentage != null ? ytOcr.ageDistribution[3].percentage + '%' : '-',
          '45-54 Age Range (YT)': ytOcr?.ageDistribution?.[4]?.percentage != null ? ytOcr.ageDistribution[4].percentage + '%' : '-',
          '55+ Age Range (YT)': ytOcr?.ageDistribution?.[5]?.percentage != null ? ytOcr.ageDistribution[5].percentage + '%' : '-',
          'YouTube Type': influencer.youtubeType || '-',
          'Engagement Rate per Reach (YT)': ytEngagement,
          'YouTube Followers': influencer.youtubeFollowers || '-',
          'Avg View (YT)': ytAvgView,
          'Youtube Views': influencer.last10ShortsViews || '-',
          'Youtube Reach': influencer.last10ShortsReach || '-',
          'YouTube Likes': influencer.youtubeLikes || '-',
          'YouTube Comments': influencer.youtubeComments || '-',
          'YouTube Shares': influencer.youtubeShares || '-',
          'Total Cost Youtube': (() => {
            const s = influencer.youtubeShortsCost;
            const v = influencer.youtubeVideoCost;
            if (!s && !v) return '-';
            return `₹${((s || 0) + (v || 0)).toLocaleString()}`;
          })(),
          'Created At': influencer.createdAt ? new Date(influencer.createdAt).toLocaleDateString('en-GB') : '-',
        };
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Completed');
      XLSX.writeFile(wb, `IY_Data_Completed_${new Date().toISOString().split('T')[0]}.xlsx`);
    } else {
      const data = rows.map(row => ({
        'SNO': row.sno,
        'Influencer ID': row.influencerCode,
        'Onboard Name': row.onboarderName,
        'Name': row.name,
        'Email': row.email,
        'State': row.state,
        'City': row.city,
        'Category': row.category,
        'Created At': row.createdAt ? new Date(row.createdAt).toLocaleString() : '-',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pending');
      XLSX.writeFile(wb, `IY_Data_Pending_${new Date().toISOString().split('T')[0]}.xlsx`);
    }
  };

  const stateOptions = [...new Set(influencers.map(i => i.state).filter(Boolean))];
  const cityOptions = [...new Set(influencers.map(i => i.city).filter(Boolean))];
  const categoryOptions = [...new Set(influencers.map(i => i.category).filter(Boolean))];

  const getFilteredData = () => {
    let filtered = influencers;

    if (user?.role !== 'admin') {
      filtered = filtered.filter(inf => inf.onboarderId?._id === user?.id);
    }

    if (filters.state) filtered = filtered.filter(inf => inf.state === filters.state);
    if (filters.city) filtered = filtered.filter(inf => inf.city === filters.city);
    if (filters.category) filtered = filtered.filter(inf => inf.category === filters.category);

    if (activeTab === 'pending') {
      return filtered.filter(inf => (!inf.ocr_status || inf.ocr_status === 'pending') && inf.negotiation_status === 1);
    }
    if (activeTab === 'completed') {
      return filtered.filter(inf => inf.ocr_status === 'completed' && new Date(inf.createdAt) <= new Date('2026-05-19T17:46:00'));
    }
  };

  const columns = [
    { field: 'sno', headerName: 'SNO', width: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
    { field: 'onboarderName', headerName: 'Onboard Name', width: 150 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'city', headerName: 'City', width: 150 },
    { field:'category', headerName: 'Category', width: 150 },
    {
      field: 'totalcostinstagram', headerName: 'Total Cost Instagram', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const value = params.row?.reelCost;
        if (!value || isNaN(value)) return '-';
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: 'AverageViewInstagram', headerName: 'Average view (Instagram)', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const totalViews = params.row?.allData?.last10ReelsViews;
        if (!totalViews || isNaN(totalViews)) return '-';
        const instagramNoOfPost = Number(params.row?.allData?.instagramNoOfPost) || 10;
        const val = Number(totalViews) / instagramNoOfPost;
        return Math.trunc(val * 100) / 100;
      }
    },
    {
      field: 'AverageReachInstagram', headerName: 'Average reach (Instagram)', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const totalReach = params.row?.allData?.last10ReelsReach;
        if (!totalReach || isNaN(totalReach)) return '-';
        const instagramNoOfPost = Number(params.row?.allData?.instagramNoOfPost) || 10;
        const val = Number(totalReach) / instagramNoOfPost;
        return Math.trunc(val * 100) / 100;
      }
    },
    {
      field: 'engagementRateInstagram', headerName: 'Engagement Rate (Instagram)', flex: 1, minWidth: 200,
      headerClassName: 'instagram-header', cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const likes = Number(params.row?.allData?.instagramLikes) || 0;
        const comments = Number(params.row?.allData?.instagramComments) || 0;
        const saves = Number(params.row?.allData?.instagramSaves) || 0;
        const shares = Number(params.row?.allData?.instagramShares) || 0;
        const reposts = Number(params.row?.allData?.instagramReposts) || 0;
        const totalReach = Number(params.row?.allData?.last10ReelsReach);
        if (!totalReach || isNaN(totalReach)) return '-';
        const instagramNoOfPost = Number(params.row?.allData?.instagramNoOfPost) || 10;
        const avgReach = totalReach / instagramNoOfPost;
        if (avgReach === 0) return '-';
        const rate = ((likes + comments + saves + shares + reposts) / avgReach) * 100;
        return Math.trunc(rate * 100) / 100;
      }
    },
    {
      field: 'totalcostyoutube', headerName: 'Total Cost Youtube', flex: 1, minWidth: 200,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const shorts = params.row?.youtubeShortsCost;
        const video = params.row?.youtubeVideoCost;
        if (!shorts || !video) return '-';
        return `₹${(shorts + video).toLocaleString()}`;
      }
    },
    {
      field: 'AverageViewYoutube', headerName: 'Average view (YouTube)', flex: 1, minWidth: 200,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const totalViews = params.row?.allData?.last10ShortsViews;
        if (!totalViews || isNaN(totalViews)) return '-';
        const youtubeNoOfPost = Number(params.row?.allData?.youtubeNoOfPost) || 10;
        const val = Number(totalViews) / youtubeNoOfPost;
        return Math.trunc(val * 100) / 100;
      }
    },
    {
      field: 'AverageReachYoutube', headerName: 'Average Reach (YouTube)', flex: 1, minWidth: 200,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const totalReach = params.row?.allData?.last10ShortsReach;
        if (!totalReach || isNaN(totalReach)) return '-';
        const youtubeNoOfPost = Number(params.row?.allData?.youtubeNoOfPost) || 10;
        const val = Number(totalReach) / youtubeNoOfPost;
        return Math.trunc(val * 100) / 100;
      }
    },
    {
      field: 'engagementRateYoutube', headerName: 'Engagement Rate (Youtube)', flex: 1, minWidth: 230,
      headerClassName: 'youtube-header', cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const likes = Number(params.row?.allData?.youtubeLikes) || 0;
        const comments = Number(params.row?.allData?.youtubeComments) || 0;
        const saves = Number(params.row?.allData?.youtubeSaves) || 0;
        const shares = Number(params.row?.allData?.youtubeShares) || 0;
        const totalReach = Number(params.row?.allData?.last10ShortsReach);
        if (!totalReach || isNaN(totalReach)) return '-';

        const youtubeNoOfPost = params.row?.allData?.youtubeNoOfPost || 10;
        const avgReach = totalReach / youtubeNoOfPost;
        if (avgReach === 0) return '-';
        const rate = ((likes + comments + shares) / avgReach) * 100;
        return Math.trunc(rate * 100) / 100;
      }
    },
    {
      field: 'createdAt', headerName: 'Created At', width: 200,
      valueGetter: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const h = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const hours = h % 12 || 12;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
      }
    },
    {
      field: 'actions', headerName: 'Actions', width: 250,
      renderCell: (params) => {
        if (activeTab === 'completed') {
          return (
            <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/influencer/${params.row._id}`)}>
              View
            </Button>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<ContentCopy />} onClick={() => handleCopyUrl(params.row.id)}>
              Copy
            </Button>
            <Button size="small" startIcon={<Email />} onClick={() => handleSendEmail(params.row.id)}>
              Email
            </Button>
            <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/influencer/${params.row._id}`)}>
              View
            </Button>
          </Box>
        );
      }
    }
  ];

  const filteredData = getFilteredData();

  const rows = filteredData.map((inf, index) => ({
    id: inf._id,
    sno: index + 1,
    influencerCode: inf.influencerCode,
    name: inf.name,
    email: inf.email,
    state: inf.state,
    city: inf.city,
    category: inf.category,
    onboarderName: inf.onboarderId?.name || 'N/A',
    reelCost: inf.reelCost,
    youtubeVideoCost: inf.youtubeVideoCost,
    youtubeShortsCost: inf.youtubeShortsCost,
    createdAt: inf.createdAt,
    _id: inf._id,
    allData: inf,
  }));

  const pendingCount = (() => {
    let filtered = influencers;
    if (user?.role !== 'admin') filtered = filtered.filter(inf => inf.onboarderId?._id === user?.id);
    if (filters.state) filtered = filtered.filter(inf => inf.state === filters.state);
    if (filters.city) filtered = filtered.filter(inf => inf.city === filters.city);
    if (filters.category) filtered = filtered.filter(inf => inf.category === filters.category);
    return filtered.filter(inf => (!inf.ocr_status || inf.ocr_status === 'pending') && inf.negotiation_status === 1).length;
  })();

  const completedCount = (() => {
    let filtered = influencers;
    if (user?.role !== 'admin') filtered = filtered.filter(inf => inf.onboarderId?._id === user?.id);
    if (filters.state) filtered = filtered.filter(inf => inf.state === filters.state);
    if (filters.city) filtered = filtered.filter(inf => inf.city === filters.city);
    if (filters.category) filtered = filtered.filter(inf => inf.category === filters.category);
    return filtered.filter(inf => inf.ocr_status === 'completed' && inf.negotiation_status === 1 && new Date(inf.createdAt) <= new Date('2026-05-19T17:46:00')).length;
  })();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        I & Y Data
      </Typography>

        <Box>
          {/* <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ color: 'white', background: '#1a1d29' }}
          >
            Filters
          </Button> */}
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
             sx={{ float: 'right' }}
          >
            Export
          </Button>
        </Box>

        {showFilters && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <Autocomplete
              options={stateOptions}
              value={filters.state}
              onChange={(e, v) => setFilters(prev => ({ ...prev, state: v }))}
              renderInput={(params) => <TextField {...params} label="State" size="small" />}
              sx={{ width: 200 }}
            />
            <Autocomplete
              options={cityOptions}
              value={filters.city}
              onChange={(e, v) => setFilters(prev => ({ ...prev, city: v }))}
              renderInput={(params) => <TextField {...params} label="City" size="small" />}
              sx={{ width: 200 }}
            />
            <Autocomplete
              options={categoryOptions}
              value={filters.category}
              onChange={(e, v) => setFilters(prev => ({ ...prev, category: v }))}
              renderInput={(params) => <TextField {...params} label="Category" size="small" />}
              sx={{ width: 200 }}
            />
            <Button variant="contained" size="small" onClick={() => setFilters({ state: null, city: null, category: null })}>
              Clear
            </Button>
          </Box>
        )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Pending (${pendingCount})`} value="pending" />
        <Tab label={`Completed (${completedCount})`} value="completed" />
      </Tabs>

      <Card sx={{ p: 2, boxShadow: 2 }}>
        <DataGrid
          columns={columns}
          rows={loading ? [] : rows}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          className="table_border"
          sx={{
            backgroundColor: 'white',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB' },
            '& .MuiDataGrid-iconSeparator': { display: 'none' },
            '& .MuiDataGrid-row': {
              backgroundColor: 'white',
              borderLeft: 'solid 1px #e5e7eb',
              borderRight: 'solid 1px #e5e7eb',
            },
            '& .instagram-cell': { backgroundColor: '#e8f4fcb7 !important' },
            '& .youtube-cell': { backgroundColor: '#fff5e69a !important' },
          }}
        />
      </Card>
    </Box>
  );
}

export default IYData;
